import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { getCardAccentColor, getCardWashColor } from '../../../utils/cardTint';

const DashboardCard = ({
  title,
  description,
  linkText = null,
  to = null,
  onClick = null,
  children = null,
  className = ''
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();

  const tintStyle = useMemo(() => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      borderTop: `3px solid ${getCardAccentColor(title)}`,
      backgroundColor: getCardWashColor(title, isDark)
    };
  }, [title]);

  const cardContent = (
    <div className="flex flex-col justify-between h-full">
      <div>
        <h3 className={`text-xl font-semibold ${semantic.contentCardHeading} mb-2`}>
          {replace(title)}
        </h3>
        <p className={`${semantic.contentCardText} mb-4`}>
          {replace(description)}
        </p>
      </div>
      {children || (linkText && (
        <span className={`${semantic.contentCardLink} no-underline hover:no-underline hover:opacity-80 transition-opacity duration-200`}>
          {replace(linkText)} →
        </span>
      ))}
    </div>
  );

  const cardClasses = `block h-full ${semantic.contentCardBorder} border p-6 rounded-lg hover:shadow-xl hover:scale-[1.03] hover:-translate-y-1 hover:border-primary-500 dark:hover:border-primary-400 transition-all duration-300 ease-out ${className}`;

  if (onClick) {
    return (
      <div
        onClick={onClick}
        className={`${cardClasses} cursor-pointer`}
        style={tintStyle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={replace(title)}
      >
        {cardContent}
      </div>
    );
  }

  if (to) {
    return (
      <Link to={to} className={cardClasses} style={tintStyle} aria-label={replace(title)}>
        {cardContent}
      </Link>
    );
  }

  return (
    <div className={cardClasses} style={tintStyle}>
      {cardContent}
    </div>
  );
};

DashboardCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  linkText: PropTypes.string,
  to: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string
};

export default DashboardCard;

